import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { navigate } from "gatsby";
import { trackCrossSellFloorplanClick } from "../utils/ga4Analytics";
import {
  hoverBig,
  responsivePaddingFloorplanImage,
  fontColorDark,
} from "./shared.module.scss";

const CrossSellFloorplanCard = ({
  floorplan,
  startPrice,        // Market price (actual/discounted rent from rent1)
  endPrice,          // Not used for cross-sell (we show single price)
  originalPrice,     // Proforma price (list/original price from rent2)
  dealText,          // Deal text from the deals API
  utilitiesIncluded,
  avDisplay = "Contact Us",
  isOnSale = false,
  index = 0,         // Position in carousel
  sourceCommunity = "", // The community page where this cross-sell appears
}) => {
  // Handle community as array or single object
  let community = floorplan.community;
  if (Array.isArray(community)) {
    community = community[0];
  }
  
  // Safety check for community data
  if (!community || !community.area || !community.citySlug || !community.slug) {
    console.error('CrossSellFloorplanCard: Missing community data', floorplan);
    return null;
  }
  
  const link = `/apartments/${community.area.toLowerCase()}/${community.citySlug.toLowerCase()}/${community.slug.toLowerCase()}/floorplans/${floorplan.floorplanSlug}/`;
  const image = getImage(floorplan.bannerImage);
  const beds = floorplan.beds != "0" ? `${floorplan.beds} bedroom` : "Studio";
  const baths = floorplan.numBaths ? floorplan.numBaths : floorplan.baths;
  const sqft = floorplan.sqFt ? floorplan.sqFt : floorplan.sqftRange;
  
  // Prepare tracking data
  const trackingData = {
    community_id: community.slug,
    community_name: community.communityName,
    floorplan_slug: floorplan.floorplanSlug,
    floorplan_name: floorplan.floorplanName || floorplan.floorplanTitle || "",
    price: startPrice || 0,
    original_price: originalPrice || 0,
    beds: Number(floorplan.beds) || 0,
    baths: Number(baths) || 0,
    sqft: Number(sqft) || 0,
    has_deal: !!dealText,
    deal_text: dealText || "",
    source_page: typeof window !== "undefined" ? window.location.pathname : "",
    source_community: sourceCommunity,
    index: index,
  };

  // Handler for tracking clicks
  const handleClick = (e) => {
    e.stopPropagation();
    trackCrossSellFloorplanClick(trackingData);
    window.open(link, '_blank');
  };
  
  return (
    <div
      className="column is-one-fifth is-floorplan-column"
      style={{ maxWidth: "273px", cursor: "pointer" }}
      onClick={handleClick}
    >
      <div
        className={`${hoverBig} card ${dealText ? 'has-deal-text' : ''}`}
        style={{ 
          textAlign: "center",
          height: "100%", 
          background: "linear-gradient(to bottom, #ffffff 0%, #ffffff 60%, #c8d4c1 85%, #c8d4c1 100%)",
          border: "1px solid #c8d4c1",
        }}
      >
        {/* Special Offer Banner - positioned above the card like regular deals */}
        {dealText && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              top: "0",
              transform: "translateY(-100%)",
            }}
          >
            <div className="floorplan-deal-text" style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              minHeight: "auto",
              lineHeight: "1.4",
              padding: "8px 10px",
            }}>{dealText}</div>
          </div>
        )}

        <div className={`${responsivePaddingFloorplanImage} card-image`}>
          <br />
          {/* Community Name at Top */}
          <h3 className="title is-4" style={{ color: "#2d4a2b", display: "block" }}>
            {community.communityName}
          </h3>
          <p className={`${fontColorDark} subtitle is-6 floorplan-subtitle`}>
            {beds}&nbsp;
            {baths} bathroom <br /> {sqft} SqFt
          </p>
          
          <figure className="image" style={{ minHeight: "250px" }}>
            {image && <GatsbyImage image={image} alt="Floorplan Image" />}
          </figure>
        </div>
        
        <div className="card-content">
          <div className="media">
            <div className="media-content">
              {/* Price Display */}
              {!startPrice || startPrice === 0 ? (
                <p className="subtitle is-6 floorplan-subtitle">
                  Contact for Pricing
                </p>
              ) : (
                <div>
                  {/* Show proforma (original list) price with strikethrough if on sale */}
                  {originalPrice && originalPrice > startPrice && (
                    <p style={{ 
                      textDecoration: "line-through", 
                      color: "#999",
                      fontSize: "0.9rem",
                      marginBottom: "0.25rem"
                    }}>
                      ${originalPrice}
                    </p>
                  )}
                  {/* Market (actual/discounted) price */}
                  <p className="subtitle is-6 floorplan-subtitle" style={{
                    color: originalPrice > startPrice ? "#e8744b" : undefined,
                    fontWeight: "bold",
                    fontSize: "1.5rem"
                  }}>
                    ${startPrice}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="content">
            {/* Nearby Special Button */}
            <button
              onClick={handleClick}
              style={{
                background: "#e8744b",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "1rem",
                fontWeight: "bold",
                cursor: "pointer",
                width: "100%",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#d66439";
                e.target.style.transform = "scale(1.02)";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "#e8744b";
                e.target.style.transform = "scale(1)";
              }}
            >
              Nearby Special
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossSellFloorplanCard;